import { useRef, useState } from "react";

import { useFormState } from "common/hooks";
import { IAddress, IGeoAddress } from "common/types";
import { AddressWriteMutation, IAddressFormValues, IVerifyAddress } from "common/types/addresses";
import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";
import { useClinicGroupCountry } from "common/contexts/UIContext";
import { CountryCode, US_DOMESTIC_COUNTRIES } from "common/constants/address";
import { buildVerifyError, classifyVerifyResponse } from "common/utils/addressVerify";

import Alert from "client/common/components/content/Alert";
import TextInput from "client/common/components/inputs/TextInput";
import StateSelect from "client/common/components/inputs/StateSelect";
import AddressAutosuggestInput from "client/common/components/inputs/AddressAutosuggestInput";
import {
  useClientUserQuery,
  useCreateAddressMutation,
  useVerifyAddressMutation,
} from "client/accounts/hooks";
import { useNewAddressConfirmationDrawer } from "client/accounts/stackables";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useSubscriptionsQuery } from "client/subscriptions/hooks";
import { useSubscriptionsBulkUpdateAddressDrawer } from "client/subscriptions/stackables";
import { NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES } from "client/subscriptions/constants";

const noop = () => undefined;

type AddressDrawerFormContentProps = {
  initialValues: IAddressFormValues;
  onSubmit: (data: AddressWriteMutation) => void;
  submitText: string;
  isLoading: boolean;
  addressCreateError: Optional<FetchError<AddressWriteMutation>>;
};

const AddressDrawerFormContent = ({
  initialValues,
  onSubmit,
  submitText,
  isLoading: isLoadingProp,
  addressCreateError,
}: AddressDrawerFormContentProps) => {
  const [values, updateField, updateFields] = useFormState(initialValues);
  const [verifyResponseError, setVerifyResponseError] = useState<IVerifyAddress | null>(null);
  const { open: openAddressConfirmationDrawer } = useNewAddressConfirmationDrawer();
  const clinicGroupCountry = useClinicGroupCountry();

  const {
    mutate: verifyAddress,
    isLoading: isVerifyLoading,
    error: addressVerifyError,
  } = useVerifyAddressMutation({
    onSuccess: (response) => {
      setVerifyResponseError(null);
      const outcome = classifyVerifyResponse(response, values);
      if (outcome.kind === "autoSave") {
        onSubmit(outcome.body);
        return;
      }
      // Always open the picker for confirmation, even when undeliverable.
      // The picker hands off to AddressUndeliverableDialog when needed.
      openAddressConfirmationDrawer({
        data: outcome.data,
        onConfirm: onSubmit,
        onUpdate: (data) => setVerifyResponseError(data),
      });
    },
  });

  const isLoading = isLoadingProp || isVerifyLoading;

  const addressTwoRef = useRef<HTMLInputElement>(null);

  const handleAddressSelect = (address: IGeoAddress) => {
    updateFields((fields) => ({ ...fields, ...address }));
    // Go to the next input for a better UX
    addressTwoRef.current?.focus();
    // User is editing again — clear any stale verify-failure copy.
    setVerifyResponseError(null);
  };

  const error = addressCreateError || addressVerifyError;
  const verifyMessage = buildVerifyError(verifyResponseError?.reason_code ?? null);
  const verifyField = verifyResponseError?.field ?? null;

  const verifyFieldError = (field: "address_one" | "address_two") =>
    verifyMessage && verifyField === field ? [verifyMessage] : undefined;

  return (
    <>
      <DrawerLayout.Form
        submitText={submitText}
        canSubmit={
          !!(
            values.name &&
            values.address_one &&
            values.city &&
            values.state &&
            values.zipcode &&
            values.country
          )
        }
        onSubmit={() => verifyAddress(values)}
        isLoading={isLoading}
        error={error}
      >
        {verifyMessage && verifyField === null && (
          <DrawerLayout.Alerts>
            <Alert variant="danger">{verifyMessage}</Alert>
          </DrawerLayout.Alerts>
        )}
        <DrawerLayout.Form.Group>
          <TextInput
            label="Full name"
            value={values.name}
            onChange={updateField("name")}
            errors={error?.errors.name}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <AddressAutosuggestInput
            label="Street address"
            value={values.address_one}
            onAddressSelect={handleAddressSelect}
            onChange={(value) => {
              updateField("address_one")(value);
              setVerifyResponseError(null);
            }}
            errors={verifyFieldError("address_one") ?? error?.errors.address_one}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <TextInput
            label="Apartment"
            ref={addressTwoRef}
            value={values.address_two}
            onChange={(value) => {
              updateField("address_two")(value);
              setVerifyResponseError(null);
            }}
            errors={verifyFieldError("address_two") ?? error?.errors.address_two}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <TextInput
            label="City"
            value={values.city}
            onChange={updateField("city")}
            errors={error?.errors.city}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <StateSelect
            label={clinicGroupCountry === CountryCode.CA ? "Province" : "State"}
            value={values.state}
            onChange={updateField("state")}
            errors={error?.errors.state}
          />
        </DrawerLayout.Form.Group>
        <DrawerLayout.Form.Group>
          <TextInput
            label={clinicGroupCountry in US_DOMESTIC_COUNTRIES ? "Zipcode" : "Postal code"}
            value={values.zipcode}
            onChange={updateField("zipcode")}
            errors={error?.errors.zipcode}
          />
        </DrawerLayout.Form.Group>
      </DrawerLayout.Form>
    </>
  );
};

type AddressDrawerFormProps = {
  isLoading?: boolean;
  onSuccess?: (address: IAddress) => void;
  isPrimary?: boolean;
  initialValues?: Partial<IAddressFormValues>;
  displaySubscriptionsBulkUpdate?: boolean;
  submitText?: string;
};

const AddressDrawerForm = ({
  isLoading: isLoadingProp,
  onSuccess = noop,
  isPrimary = false,
  initialValues,
  displaySubscriptionsBulkUpdate = false,
  submitText = "Add Address",
}: AddressDrawerFormProps) => {
  const { open: openSubscriptionBulkUpdate } = useSubscriptionsBulkUpdateAddressDrawer();
  const {
    data: clientUser,
    isLoading: isClientUserLoading,
    error: clientUserError,
  } = useClientUserQuery();
  const clinicGroupCountry = useClinicGroupCountry();

  const {
    data: subscriptions = [],
    isLoading: isSubscriptionsLoading,
    error: subscriptionsError,
  } = useSubscriptionsQuery(
    { visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES },
    { enabled: displaySubscriptionsBulkUpdate },
  );

  const onCreateAddressSuccess = (newAddress: IAddress) => {
    onSuccess(newAddress);
    if (displaySubscriptionsBulkUpdate && subscriptions.length >= 1) {
      openSubscriptionBulkUpdate({
        address: newAddress,
        subscriptions,
      });
    }
  };

  const {
    mutate: createAddress,
    isLoading: isCreating,
    error,
  } = useCreateAddressMutation({
    onSuccess: onCreateAddressSuccess,
  });

  if (isClientUserLoading || isSubscriptionsLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!clientUser || clientUserError || subscriptionsError) {
    return (
      <DrawerLayout.FetchError entityName="account" error={clientUserError || subscriptionsError} />
    );
  }

  return (
    <AddressDrawerFormContent
      isLoading={isLoadingProp || isCreating || isSubscriptionsLoading}
      submitText={submitText}
      initialValues={{
        name: clientUser?.full_name ?? "",
        address_one: "",
        address_two: "",
        city: "",
        state: "",
        county: "",
        zipcode: "",
        country: clinicGroupCountry.toString(),
        ...initialValues,
      }}
      addressCreateError={error}
      onSubmit={(values) => createAddress({ is_primary: isPrimary, ...values })}
    />
  );
};

export default AddressDrawerForm;
